import { useBroadcastChannel, type UseBroadcastChannelReturn } from '@vueuse/core';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { type App, watch } from 'vue';
import { useAuthStore } from '@/parts/auth/store/auth';

const appHost =
  process.env.NODE_ENV === 'development' ? `${process.env.VUE_HOST}:${process.env.VUE_PORT}/` : process.env.VUE_HOST;

/**
 * Config for the oidc client.
 */
const userManager = new UserManager({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  scheme: 'openIDConnect',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  authority: process.env.SSO_HOST as string,
  client_id: 'extranet',
  redirect_uri: `${appHost}login`,
  post_logout_redirect_uri: process.env.LOGOUT_REDIRECT_URI,
  response_type: 'code',
  scope: 'IdentityServerApi ARS offline_access openid profile digest',
  automaticSilentRenew: true,
  grantType: 'authorization_code',
  revokeTokenTypes: ['refresh_token', 'access_token'],
  idToken: {
    property: 'id_token',
    maxAge: 900,
  },
  codeChallengeMethod: 'S256',
  silentRequestTimeoutInSeconds: 10,
  includeIdTokenInSilentRenew: true,
  stopCheckSessionOnError: true,
  includeIdTokenInSilentSignout: true,
});

/**
 * Class to encapsulate all authentication related logic.
 */
export class AuthService {
  private broadcastEmitter: UseBroadcastChannelReturn<{ isLogout: boolean }, { isLogout: boolean }>;

  private isLoggingOut = false;

  private userIdToken = '';

  constructor(private readonly app: App) {
    this.broadcastEmitter = app.runWithContext(() =>
      useBroadcastChannel({
        name: 'auth',
      }),
    );

    const { data } = this.broadcastEmitter;

    watch(data, (newValue) => {
      if (newValue.isLogout) {
        location.reload();
      }
    });

    userManager.events.addUserLoaded((user) => {
      if (user.id_token) {
        this.userIdToken = user.id_token;
      }
    });
  }
  /**
   * Initiate the login process.
   */
  async login() {
    this.app.runWithContext(() => {
      const { setAfterLoginPath } = useAuthStore();

      setAfterLoginPath();
    });

    return userManager.signinRedirect().catch((error) => console.error(error));
  }

  async logout() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    this.broadcastEmitter.post({ isLogout: true });

    return userManager.signoutRedirect({
      id_token_hint: this.userIdToken,
    });
  }

  /**
   * Handles the redirect from the OAuth server after a user logged in.
   */
  handleLoginRedirect() {
    return userManager.signinRedirectCallback();
  }

  /**
   * Handles the redirect from the OAuth server after a user logged out.
   */
  handleLogoutRedirect() {
    return userManager.signoutRedirectCallback();
  }

  /**
   * Checks whether or not a user is currently logged in.
   *
   * Returns a promise which will be resolved to true/false or be rejected with an error.
   */
  isUserLoggedIn() {
    return new Promise((resolve, reject) => {
      userManager
        .getUser()
        .then((user) => {
          if (user === null) {
            resolve(false);
          }
          resolve(true);
        })
        .catch((error) => reject(error));
    });
  }

  /**
   * Get the profile data for the currently authenticated user.
   *
   * Returns an empty object if no user is logged in.
   */
  getProfile() {
    return new Promise((resolve, reject) => {
      userManager
        .getUser()
        .then((user) => {
          if (user === null) {
            resolve(null);
          }
          resolve(user?.profile);
        })
        .catch((error) => reject(error));
    });
  }

  /**
   * Get the access token.
   *
   * Can be used to make requests to the backend.
   */
  getAccessToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      userManager
        .getUser()
        .then((user) => {
          resolve(`${user?.token_type} ${user?.access_token}`);
        })
        .catch((error) => reject(error));
    });
  }
}

export function authPlugin(app: App) {
  app.config.globalProperties.$auth = new AuthService(app);
}
